import React, { useState, useEffect, useRef, useCallback } from "react";
import { getConversations, getConversation, sendMessage, markMessagesAsRead, getGroupConversation, sendGroupMessage, markGroupMessagesAsRead, getUser } from "../services/authService";
import { getPhotoUrl } from "../services/apiConfig";
import CreateGroupModal from "./CreateGroupModal";
import GroupSettingsModal from "./GroupSettingsModal";

// Stable identity for a conversation (group vs 1:1)
function convKey(c) {
	if (!c) return null;
	return c.isGroup ? `g${c.idGroup}` : `p${c.partnerId}`;
}

// Normalize a backend message (1:1 or group) into a uniform shape for rendering
function normalizeMessage(m) {
	return {
		idMessage: m.idMessage != null ? m.idMessage : m.idGroupMessage,
		idSender: m.idSender,
		contenu: m.contenu,
		dateEnvoi: m.dateEnvoi,
		lu: m.lu,
		senderPrenom: m.senderPrenom || null,
	};
}

const AVATAR_GRADIENTS = [
	"linear-gradient(135deg, #30a49a, #2a8f87)",
	"linear-gradient(135deg, #F87B1B, #d4600e)",
	"linear-gradient(135deg, #6C5CE7, #A29BFE)",
	"linear-gradient(135deg, #e17055, #c75e44)",
	"linear-gradient(135deg, #00b894, #009e7c)",
	"linear-gradient(135deg, #fd79a8, #e84393)",
	"linear-gradient(135deg, #0984e3, #0764b8)",
	"linear-gradient(135deg, #55efc4, #00b894)",
];

function getAvatarGradient(name) {
	let hash = 0;
	for (let i = 0; i < name.length; i++) {
		hash = name.charCodeAt(i) + ((hash << 5) - hash);
	}
	return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}

function getInitials(prenom, nom) {
	const a = (prenom || "?")[0] || "?";
	const b = (nom || "")[0] || "";
	return (a + b).toUpperCase();
}

function formatDate(dateStr) {
	if (!dateStr) return "";
	const date = new Date(dateStr);
	const now = new Date();
	const diffMs = now - date;
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
	if (diffDays === 0) {
		return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
	} else if (diffDays === 1) {
		return "Hier";
	} else if (diffDays < 7) {
		return date.toLocaleDateString("fr-FR", { weekday: "short" });
	} else {
		return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
	}
}

function formatMessageTime(dateStr) {
	if (!dateStr) return "";
	const date = new Date(dateStr);
	return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function formatDayLabel(dateStr) {
	if (!dateStr) return "";
	const date = new Date(dateStr);
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const target = new Date(date);
	target.setHours(0, 0, 0, 0);
	const diffDays = Math.round((today - target) / (1000 * 60 * 60 * 24));
	if (diffDays === 0) return "Aujourd'hui";
	if (diffDays === 1) return "Hier";
	if (diffDays < 7) return date.toLocaleDateString("fr-FR", { weekday: "long" });
	return date.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: today.getFullYear() === date.getFullYear() ? undefined : "numeric" });
}

function isSameDay(a, b) {
	if (!a || !b) return false;
	const da = new Date(a);
	const db = new Date(b);
	return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
}

function Avatar({ prenom, nom, photo, size = "md", isGroup = false }) {
	const [photoFailed, setPhotoFailed] = useState(false);
	const initials = getInitials(prenom, nom);
	const gradient = getAvatarGradient((prenom || "") + (nom || ""));
	const photoUrl = photo && !photoFailed ? getPhotoUrl(photo) : null;
	const sizeClass = size === "sm" ? "messaging-avatar-sm" : size === "xs" ? "messaging-avatar-xs" : "";

	if (isGroup) {
		return (
			<div className={`messaging-avatar ${sizeClass}`} style={{ background: gradient }}>
				<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: "58%", height: "58%" }}>
					<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
					<circle cx="9" cy="7" r="4" />
					<path d="M23 21v-2a4 4 0 0 0-3-3.87" />
					<path d="M16 3.13a4 4 0 0 1 0 7.75" />
				</svg>
			</div>
		);
	}

	return (
		<div className={`messaging-avatar ${sizeClass}`} style={{ background: gradient }}>
			{photoUrl ? (
				<img src={photoUrl} alt="" onError={() => setPhotoFailed(true)} />
			) : (
				initials
			)}
		</div>
	);
}

export default function MessagingModal({ visible, onClose, initialPartner = null }) {
	const [conversations, setConversations] = useState([]);
	const [activeConversation, setActiveConversation] = useState(null);
	const [messages, setMessages] = useState([]);
	const [newMessage, setNewMessage] = useState("");
	const [loading, setLoading] = useState(false);
	const [loadingMessages, setLoadingMessages] = useState(false);
	const [hasMore, setHasMore] = useState(false);
	const [page, setPage] = useState(1);
	const [sending, setSending] = useState(false);

	// Group management modals
	const [showCreateGroup, setShowCreateGroup] = useState(false);
	const [groupSettingsFor, setGroupSettingsFor] = useState(null); // idGroup

	const messagesEndRef = useRef(null);
	const messagesContainerRef = useRef(null);
	const pollIntervalRef = useRef(null);
	const currentUser = getUser();

	const loadConversations = useCallback(async () => {
		const result = await getConversations();
		if (result.success) {
			setConversations(result.conversations || []);
		}
	}, []);

	const loadMessages = useCallback(async (conv, pageNum = 1, append = false) => {
		if (!conv) return;
		setLoadingMessages(true);
		const result = conv.isGroup
			? await getGroupConversation(conv.idGroup, pageNum, 30)
			: await getConversation(conv.partnerId, pageNum, 30);
		if (result.success) {
			// API returns newest-first; flip to chronological (oldest → newest) for chat display
			const msgs = [...(result.messages || [])].map(normalizeMessage).reverse();
			if (append) {
				// older page = older messages → prepend
				setMessages((prev) => [...msgs, ...prev]);
			} else {
				setMessages(msgs);
				setTimeout(() => {
					if (messagesEndRef.current) {
						messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
					}
				}, 50);
			}
			setHasMore(result.hasMore || false);
		}
		setLoadingMessages(false);
	}, []);

	const openConversation = useCallback(async (conv) => {
		setActiveConversation(conv);
		setPage(1);
		setMessages([]);
		setHasMore(false);
		await loadMessages(conv, 1, false);
		if (conv.isGroup) {
			await markGroupMessagesAsRead(conv.idGroup);
		} else {
			await markMessagesAsRead(conv.partnerId);
		}
		setConversations((prev) =>
			prev.map((c) => (convKey(c) === convKey(conv) ? { ...c, unreadCount: 0 } : c))
		);
	}, [loadMessages]);

	const handleLoadMore = async () => {
		if (!activeConversation || loadingMessages) return;
		const nextPage = page + 1;
		setPage(nextPage);
		await loadMessages(activeConversation, nextPage, true);
	};

	const handleSend = async () => {
		if (!newMessage.trim() || !activeConversation || sending) return;
		const content = newMessage.trim();
		setNewMessage("");
		setSending(true);

		const optimisticMsg = {
			idMessage: `tmp-${Date.now()}`,
			idSender: currentUser?.id,
			senderPrenom: currentUser?.prenom || null,
			contenu: content,
			dateEnvoi: new Date().toISOString(),
			lu: false,
			isOptimistic: true,
		};
		setMessages((prev) => [...prev, optimisticMsg]);
		setTimeout(() => {
			if (messagesEndRef.current) {
				messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
			}
		}, 50);

		const result = activeConversation.isGroup
			? await sendGroupMessage(activeConversation.idGroup, content)
			: await sendMessage(activeConversation.partnerId, content);
		if (result.success) {
			setMessages((prev) =>
				prev.map((m) =>
					m.idMessage === optimisticMsg.idMessage
						? { ...m, idMessage: result.idMessage, isOptimistic: false }
						: m
				)
			);
			loadConversations();
		} else {
			setMessages((prev) => prev.filter((m) => m.idMessage !== optimisticMsg.idMessage));
		}
		setSending(false);
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	useEffect(() => {
		if (!visible) return;
		setLoading(true);
		loadConversations().finally(() => setLoading(false));

		pollIntervalRef.current = setInterval(() => {
			loadConversations();
			if (activeConversation) {
				loadMessages(activeConversation, 1, false);
			}
		}, 30000);

		return () => {
			if (pollIntervalRef.current) {
				clearInterval(pollIntervalRef.current);
				pollIntervalRef.current = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible]);

	useEffect(() => {
		if (!visible) {
			setActiveConversation(null);
			setMessages([]);
			setConversations([]);
			setNewMessage("");
			setPage(1);
		}
	}, [visible]);

	// Auto-open conversation when an initialPartner is provided (eg. from another part of the app).
	useEffect(() => {
		if (!visible || !initialPartner || !initialPartner.partnerId) return;
		if (activeConversation?.partnerId === initialPartner.partnerId) return;
		openConversation(initialPartner);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible, initialPartner]);

	if (!visible) return null;

	const messageRows = messages.map((msg, i) => {
		const prev = messages[i - 1];
		const next = messages[i + 1];
		const firstOfGroup = !prev || prev.idSender !== msg.idSender || !isSameDay(prev.dateEnvoi, msg.dateEnvoi);
		const lastOfGroup = !next || next.idSender !== msg.idSender || !isSameDay(next.dateEnvoi, msg.dateEnvoi);
		const showDaySeparator = !prev || !isSameDay(prev.dateEnvoi, msg.dateEnvoi);
		return { msg, firstOfGroup, lastOfGroup, showDaySeparator };
	});

	return (
		<div
			className="messaging-overlay"
			onClick={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
		>
			<div className="messaging-modal" onClick={(e) => e.stopPropagation()}>
				<div className="messaging-inner">
					<div className="messaging-header">
						<h2 className="messaging-title">
							<span className="messaging-title-dot" />
							Messages
						</h2>
						<button className="messaging-close" onClick={onClose} aria-label="Fermer">
							&times;
						</button>
					</div>

					<div className={`messaging-body ${activeConversation ? "has-active" : ""}`}>
						<div className="messaging-sidebar">
							<button
								className="messaging-new-group-btn"
								onClick={() => setShowCreateGroup(true)}
								type="button"
							>
								+ Nouveau groupe
							</button>
							{loading && (
								<div className="messaging-empty-state">Chargement...</div>
							)}
							{!loading && conversations.length === 0 && (
								<div className="messaging-empty-state">Aucune conversation</div>
							)}
							{conversations.map((conv) => {
								const isActive = convKey(activeConversation) === convKey(conv);
								const hasUnread = conv.unreadCount > 0;
								const displayName = conv.isGroup ? conv.groupNom : `${conv.partnerPrenom} ${conv.partnerNom}`;
								const rawPreview = conv.dernierMessage || "";
								const preview = conv.isGroup && rawPreview
									? `${conv.isLastMessageMine ? "Vous" : (conv.lastSenderPrenom || "")}${conv.isLastMessageMine || conv.lastSenderPrenom ? " : " : ""}${rawPreview}`
									: rawPreview;
								return (
									<div
										key={convKey(conv)}
										className={`messaging-conversation ${isActive ? "active" : ""}`}
										onClick={() => openConversation(conv)}
									>
										<Avatar
											prenom={conv.isGroup ? conv.groupNom : conv.partnerPrenom}
											nom={conv.isGroup ? "" : conv.partnerNom}
											photo={conv.isGroup ? null : conv.partnerPhoto}
											isGroup={conv.isGroup}
										/>
										<div className="messaging-conv-info">
											<div className="messaging-conv-row">
												<span className={`messaging-conv-name ${hasUnread ? "unread" : ""}`}>
													{displayName}
												</span>
												<span className="messaging-conv-date">
													{formatDate(conv.dateDernierMessage)}
												</span>
											</div>
											<div className="messaging-conv-row">
												<span className={`messaging-conv-preview ${hasUnread ? "unread" : ""}`}>
													{preview}
												</span>
												{hasUnread && (
													<span className="messaging-unread-badge">
														{conv.unreadCount > 99 ? "99+" : conv.unreadCount}
													</span>
												)}
											</div>
										</div>
									</div>
								);
							})}
						</div>

						<div className="messaging-main">
							{!activeConversation ? (
								<div className="messaging-placeholder">
									<div className="messaging-placeholder-icon">✉</div>
									<div className="messaging-placeholder-text">Sélectionnez une conversation</div>
									<div className="messaging-placeholder-hint">
										Choisissez un contact dans la liste pour commencer à discuter
									</div>
								</div>
							) : (
								<>
									<div className="messaging-conv-header">
										<Avatar
											prenom={activeConversation.isGroup ? activeConversation.groupNom : activeConversation.partnerPrenom}
											nom={activeConversation.isGroup ? "" : activeConversation.partnerNom}
											photo={activeConversation.isGroup ? null : activeConversation.partnerPhoto}
											isGroup={activeConversation.isGroup}
											size="sm"
										/>
										<div className="messaging-conv-header-info">
											<span className="messaging-conv-header-name">
												{activeConversation.isGroup ? activeConversation.groupNom : `${activeConversation.partnerPrenom} ${activeConversation.partnerNom}`}
											</span>
											{activeConversation.isGroup && activeConversation.memberCount > 0 && (
												<span className="messaging-conv-header-sub">{activeConversation.memberCount} membre{activeConversation.memberCount > 1 ? "s" : ""}</span>
											)}
										</div>
										{activeConversation.isGroup && (
											<button
												type="button"
												className="messaging-group-settings-btn"
												onClick={() => setGroupSettingsFor(activeConversation.idGroup)}
												title="Paramètres du groupe"
												aria-label="Paramètres du groupe"
											>
												<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
													<circle cx="12" cy="12" r="3" />
													<path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
												</svg>
											</button>
										)}
									</div>

									<div ref={messagesContainerRef} className="messaging-messages">
										{hasMore && (
											<div className="messaging-load-more-wrap">
												<button
													className="messaging-load-more"
													onClick={handleLoadMore}
													disabled={loadingMessages}
												>
													{loadingMessages ? "Chargement..." : "Charger plus"}
												</button>
											</div>
										)}

										{loadingMessages && messages.length === 0 && (
											<div className="messaging-empty-state">Chargement...</div>
										)}

										{messageRows.map(({ msg, firstOfGroup, lastOfGroup, showDaySeparator }) => {
											const isMine = msg.idSender === currentUser?.id || msg.idSender === currentUser?.Id;
											return (
												<React.Fragment key={msg.idMessage}>
													{showDaySeparator && (
														<div className="messaging-day-separator">
															<span>{formatDayLabel(msg.dateEnvoi)}</span>
														</div>
													)}
													<div
														className={`messaging-message-row ${isMine ? "mine" : ""} ${firstOfGroup ? "first-of-group" : ""}`}
													>
														{!isMine && (
															lastOfGroup ? (
																<Avatar
																	prenom={activeConversation.isGroup ? msg.senderPrenom : activeConversation.partnerPrenom}
																	nom={activeConversation.isGroup ? "" : activeConversation.partnerNom}
																	photo={activeConversation.isGroup ? null : activeConversation.partnerPhoto}
																	size="xs"
																/>
															) : (
																<div className="messaging-avatar-slot" />
															)
														)}
														<div className="messaging-bubble-wrap">
															{activeConversation.isGroup && !isMine && firstOfGroup && msg.senderPrenom && (
																<div className="messaging-bubble-sender">{msg.senderPrenom}</div>
															)}
															<div className={`messaging-bubble ${isMine ? "mine" : ""} ${lastOfGroup ? "tail" : ""} ${msg.isOptimistic ? "optimistic" : ""}`}>
																<div className="messaging-bubble-text">{msg.contenu}</div>
															</div>
															{lastOfGroup && (
																<div className="messaging-bubble-time">{formatMessageTime(msg.dateEnvoi)}</div>
															)}
														</div>
													</div>
												</React.Fragment>
											);
										})}
										<div ref={messagesEndRef} />
									</div>

									<div className="messaging-input-bar">
										<div className="messaging-input-wrap">
											<input
												type="text"
												className="messaging-input"
												value={newMessage}
												onChange={(e) => setNewMessage(e.target.value)}
												onKeyDown={handleKeyDown}
												placeholder="Écrire un message..."
											/>
										</div>
										<button
											className="messaging-send-btn"
											onClick={handleSend}
											disabled={!newMessage.trim() || sending}
											aria-label="Envoyer"
										>
											<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path d="M3 12L21 3L17 12L21 21L3 12Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="currentColor" />
											</svg>
										</button>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</div>

			{showCreateGroup && (
				<CreateGroupModal
					onClose={() => setShowCreateGroup(false)}
					onCreated={async (idGroup) => {
						setShowCreateGroup(false);
						await loadConversations();
						const list = await getConversations();
						if (list.success) {
							const fresh = (list.conversations || []).find((c) => c.isGroup && c.idGroup === idGroup);
							if (fresh) setActiveConversation(fresh);
						}
					}}
				/>
			)}

			{groupSettingsFor != null && (
				<GroupSettingsModal
					idGroup={groupSettingsFor}
					currentUserId={currentUser?.id || currentUser?.Id}
					onClose={() => setGroupSettingsFor(null)}
					onGroupUpdated={loadConversations}
					onGroupLeft={() => {
						setGroupSettingsFor(null);
						setActiveConversation(null);
						loadConversations();
					}}
				/>
			)}
		</div>
	);
}
